/**
 * Lyrics Detection Utility
 *
 * Detects special rendering modes from lyrics content:
 * - Diamond: Detected by [diamond]...[/diamond] tags
 * - Acrostic: Detected by %...% markers
 *
 * This is a deliberate mirror of jnana-native's
 * src/util/lyricsDetection.ts and jnana-admin's src/utils/lyricsDetection.ts.
 * The three copies must stay byte-equivalent in behavior — the exported names,
 * the LyricsAnalysis shape, and every regex here are intentionally identical.
 * Divergence is a bug; change all three together.
 */

export interface LyricsAnalysis {
  // Diamond detection
  hasDiamondSection: boolean;
  normalLyrics: string;
  diamondLyrics: string;
  // Acrostic detection
  hasAcrosticMarkers: boolean;
}

/**
 * Analyze lyrics text to detect diamond and acrostic patterns.
 *
 * @param lyricsText - The raw lyrics text to analyze
 * @returns Analysis result with detected patterns and extracted content
 */
export function analyzeLyrics(lyricsText: string): LyricsAnalysis {
  if (!lyricsText) {
    return {
      hasDiamondSection: false,
      normalLyrics: '',
      diamondLyrics: '',
      hasAcrosticMarkers: false,
    };
  }

  // Detect acrostic markers (%text%)
  const hasAcrosticMarkers = /%[^%]+%/.test(lyricsText);

  // Detect and extract diamond sections (case-insensitive)
  // Uses [diamond]...[/diamond] syntax to avoid WAF XSS filtering
  const diamondMatch = lyricsText.match(/\[diamond\]([\s\S]*?)\[\/diamond\]/i);

  if (!diamondMatch) {
    return {
      hasDiamondSection: false,
      normalLyrics: lyricsText,
      diamondLyrics: '',
      hasAcrosticMarkers,
    };
  }

  const diamondContent = diamondMatch[1].trim();
  const normalContent = lyricsText.replace(/\[diamond\][\s\S]*?\[\/diamond\]/i, '').trim();

  return {
    hasDiamondSection: true,
    normalLyrics: normalContent,
    diamondLyrics: diamondContent,
    hasAcrosticMarkers,
  };
}

/**
 * Strip acrostic markers from text for display.
 * Removes the % markers and returns plain text.
 *
 * @param text - Text potentially containing %...% markers
 * @returns Text with markers removed
 */
export function stripAcrosticMarkers(text: string): string {
  return text.replace(/%([^%]+)%/g, '$1');
}
